import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Row, Col, message, Modal } from 'antd';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BannerService from 'services/banner';
import { get } from 'lodash';

import PdButton from 'components/pd-button';

import './style.scss';

const { Content } = Layout;
const { confirm } = Modal;

export function Banners() {
  const [bannerListActive, setBannerListActive] = useState([]);
  const [bannerListInactive, setBannerListInactive] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );
  async function fetchBannerActive() {
    const { data } = await BannerService.getBannerList('active');
    const bannerList = data.data;
    setBannerListActive(bannerList);
  }

  async function fetchBannerInactive() {
    const { data } = await BannerService.getBannerList('inactive');
    const bannerList = data.data;
    setBannerListInactive(bannerList);
  }

  async function fetchBannerList() {
    await fetchBannerActive();
    await fetchBannerInactive();
  }

  useEffect(() => {
    fetchBannerList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function hideBanner(id) {
    try {
      if (id) {
        await BannerService.hideBanner(id);
        message.success('successfully hide banner');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      fetchBannerList();
    }
  }

  async function deleteBanner(id) {
    try {
      if (id) {
        await BannerService.deleteBanner(id);
        message.success('Successfully deleted banner');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      fetchBannerList();
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure you want to hide this banner?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hideBanner(id);
      },
      onCancel() {},
    });
  }

  function handleDelete(id) {
    confirm({
      title: 'Are you sure you want to delete?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteBanner(id);
      },
      onCancel() {},
    });
  }

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  async function handleRestore(id) {
    try {
      if (id) {
        await BannerService.restoreBanner(id);
        message.success('Successfully restored banner');
      }
    } catch (err) {
      const errMessage = get(err, 'response.data.message', '');
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      fetchBannerList();
    }
  }

  function SortableItem({ value: banner, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: banner.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginTop: 20,
      userSelect: 'none',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="pd-cms-banners--list draggable-item">
        <Row type="flex" align="middle" gutter={30}>
          <Col span={1}>
            <div {...listeners} style={{ cursor: 'grab', padding: '0 10px' }}>
              ⋮⋮
            </div>
          </Col>
          <Col span={6} {...listeners}>
            <div className="pd-cms-banners--picture">
              <img className="image" src={banner.image_url} alt="banners" />
            </div>
          </Col>
          <Col span={9} {...listeners}>
            <div className="pd-cms-banners--title">
              <span className="table-title--sort">{banner.title}</span>
            </div>
          </Col>
          <Col span={2} {...listeners}>
            <div className="status-indicator">
              <span
                className={banner.status !== 'inactive' ? 'active' : 'hidden'}>
                {displayStatus(banner.status)}
              </span>
            </div>
          </Col>
          <Col span={6}>
            <div className="button-table">
              <Row>
                <Col span={12} className="button">
                  {banner.status === 'active' ? (
                    <PdButton
                      type="danger"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHide(banner.id);
                      }}>
                      Deactivate
                    </PdButton>
                  ) : (
                    <PdButton
                      type="primary"
                      outline
                      full
                      style={{ height: 32 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(banner.id);
                      }}>
                      Reactivate
                    </PdButton>
                  )}
                </Col>
                <Col span={12} className="button">
                  {banner.status === 'active' ? (
                    <Link
                      to={{
                        pathname: "/banners/edit", search: `id=${banner.id}`,
                      }}>
                      <PdButton type="primary" full style={{ height: 32 }}>
                        Edit
                      </PdButton>
                    </Link>
                  ) : (
                    <PdButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(banner.id);
                      }}
                      type="danger"
                      full
                      style={{ height: 32 }}>
                      Delete
                    </PdButton>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem key={item.id} index={index} value={item} />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  async function onSortEndActive({ oldIndex, newIndex }) {
    let list = [...bannerListActive];
    let temp = list[oldIndex];
    list.splice(oldIndex, 1);
    list.splice(newIndex, 0, temp);

    let payload = [];
    list.forEach((item, index) => {
      let order_attr = {
        id: item.id,
        order_level: index,
      };
      payload.push(order_attr);
    });

    try {
      setBannerListActive(list);
      await BannerService.updateBannerOrder(payload);
      message.success('Successfully updated banner order');
    } catch {
      message.error('Cannot connect to server, please check connection');
    }
  }

  return (
    <Layout className="pd-cms-banners main-container">
      <div className="content">
        <div className="pageTitle">Banners</div>
        <Content className="pageContent">
          <Row gutter={20}>
            <Col span={17}></Col>

            <Col span={7} className="float-right">
              <Link to={'/banners/create'}>
                <PdButton
                  type="primary"
                  outline
                  full
                  style={{ height: 32, width: 176 }}>
                  Add New Banner
                </PdButton>
              </Link>
            </Col>
            <Col span={24}>
              <div id="drag-content-active">
                <SortableList
                  items={bannerListActive}
                  onSortEnd={onSortEndActive}
                />
              </div>
            </Col>

            {bannerListInactive.length > 0 && (
              <Col span={24} className="hidden-title-container">
                <div className="pageTitle hiddenTitle">Inactive Banners</div>
              </Col>
            )}

            <Col span={24}>
              <div id="drag-content-inactive">
                <SortableList items={bannerListInactive} />
              </div>
            </Col>
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { Banners };
